import React from 'react'
import { Box, Button, Typography, alpha } from '@mui/material'
import Grid from '@mui/material/Grid'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import FileItemCard from './FileItemCard'
import FolderCard from '../../../features/folders/components/FolderCard'
import EmptyState from '../../../shared/components/layout/EmptyState'
import { FileRowSkeleton, FileGridSkeleton } from '../../../shared/components/feedback/Skeleton'
import { FileItem } from '../../../services/api'
import type { TranscriptionFolder } from '../../../types/folder'

export type ViewMode = 'grid' | 'list'

type Props = {
  files: FileItem[]
  viewMode: ViewMode
  loading?: boolean
  hasError?: boolean // Quando há erro, não mostra EmptyState
  onOpen: (file: FileItem) => void
  onDelete: (fileId: string) => void
  onUploadClick: () => void
  // Selection props
  selectionMode?: boolean
  selectedFiles?: FileItem[]
  onSelectFile?: (file: FileItem) => void
  // Folder props
  folders?: TranscriptionFolder[]
  onFolderClick?: (folderId: string) => void
  onFolderRename?: (folder: TranscriptionFolder) => void
  onFolderMove?: (folder: TranscriptionFolder) => void
  onFolderDelete?: (folder: TranscriptionFolder) => void
  onFolderChangeColor?: (folder: TranscriptionFolder) => void
  onDropItemsToFolder?: (targetFolderId: string, itemIds: string[], itemType: 'file' | 'folder') => void
  /** Mover arquivo individual para pasta */
  onMoveToFolder?: (file: FileItem) => void
  /** Renomear arquivo (displayName) */
  onRename?: (file: FileItem) => void
  /** Se está dentro de uma pasta */
  insideFolder?: boolean
  folderName?: string
  onCreateFolder?: () => void
}

export default function FileList({
  files,
  viewMode,
  loading,
  hasError,
  onOpen,
  onDelete,
  onUploadClick,
  selectionMode = false,
  selectedFiles = [],
  onSelectFile,
  folders = [],
  onFolderClick,
  onFolderRename,
  onFolderMove,
  onFolderDelete,
  onFolderChangeColor,
  onDropItemsToFolder,
  onMoveToFolder,
  onRename,
  insideFolder = false,
  folderName,
  onCreateFolder
}: Props) {
  const selectedIds = new Set(selectedFiles.map(f => f.fileId))

  // Loading state
  if (loading) {
    if (viewMode === 'grid') {
      return (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
              <FileGridSkeleton />
            </Grid>
          ))}
        </Grid>
      )
    }
    return (
      <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden', '#monkee-file-transcript-client &': { overflow: 'hidden !important' } }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <FileRowSkeleton key={i} />
        ))}
      </Box>
    )
  }

  // Empty state - só mostra se não houver erro
  if (!files.length && !folders.length && !hasError) {
    return (
      <EmptyState
        onUploadClick={onUploadClick}
        insideFolder={insideFolder}
        folderName={folderName}
        onCreateFolder={onCreateFolder}
      />
    )
  }
  
  // Se não tem arquivos/pastas e tem erro, não renderiza nada (o erro é mostrado no App)
  if (!files.length && !folders.length && hasError) {
    return null
  }

  const hasFolders = folders.length > 0

  // Grid view
  if (viewMode === 'grid') {
    return (
      <Box>
        {/* Botão criar pasta - sempre visível */}
        {onCreateFolder && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CreateNewFolderIcon />}
              onClick={onCreateFolder}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                color: 'text.secondary',
                borderColor: 'divider',
                borderStyle: 'dashed',
                py: 1,
                px: 2,
                '&:hover': {
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  bgcolor: alpha('#1976d2', 0.04)
                },
                // Tema WP aplica background escuro em button:focus/active e fica preso.
                '#monkee-file-transcript-client &:focus': { backgroundColor: 'transparent !important', outline: 'none !important' },
                '#monkee-file-transcript-client &:focus-visible': { backgroundColor: 'transparent !important', outline: 'none !important' },
                '#monkee-file-transcript-client &:active': { backgroundColor: `${alpha('#1976d2', 0.08)} !important` },
                '#monkee-file-transcript-client &:hover': { backgroundColor: `${alpha('#1976d2', 0.04)} !important` },
              }}
            >
              Criar pasta
            </Button>
          </Box>
        )}

        {/* Pastas */}
        {hasFolders && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#999', fontWeight: 600 }}>
                Pastas
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: files.length > 0 ? 3 : 0 }}>
              {folders.map((folder, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={folder.id}>
                  <FolderCard
                    folder={folder}
                    view="grid"
                    onClick={onFolderClick || (() => {})}
                    onRename={onFolderRename}
                    onMove={onFolderMove}
                    onDelete={onFolderDelete}
                    onChangeColor={onFolderChangeColor}
                    onDropItems={onDropItemsToFolder}
                    animationDelay={index * 30}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Separador e arquivos */}
        {files.length > 0 && (
          <>
            {hasFolders && (
              <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, mb: 1, display: 'block', px: 0.5 }}>
                Arquivos
              </Typography>
            )}
            <Grid container spacing={2}>
              {files.map(file => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={file.fileId} data-select-id={file.fileId}>
                  <FileItemCard
                    file={file}
                    viewMode={viewMode}
                    onOpen={onOpen}
                    onDelete={onDelete}
                    selectionMode={selectionMode}
                    selected={selectedIds.has(file.fileId)}
                    onSelect={onSelectFile}
                    selectedFileIds={Array.from(selectedIds)}
                    draggable
                    onMoveToFolder={onMoveToFolder}
                    onRename={onRename}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    )
  }

  // List view
  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden', '#monkee-file-transcript-client &': { overflow: 'hidden !important' } }}>
      {/* Botão criar pasta - sempre visível */}
      {onCreateFolder && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: '1px solid #f0f0f0' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CreateNewFolderIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            onClick={onCreateFolder}
            sx={{
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              color: 'text.secondary',
              borderColor: 'divider',
              borderStyle: 'dashed',
              py: { xs: 0.5, sm: 0.75 },
              px: { xs: 1.5, sm: 2 },
              '&:hover': {
                color: 'primary.main',
                borderColor: 'primary.main',
                bgcolor: alpha('#1976d2', 0.04)
              },
              // Tema WP aplica background escuro em button:focus/active e fica preso.
              '#monkee-file-transcript-client &:focus': { backgroundColor: 'transparent !important', outline: 'none !important' },
              '#monkee-file-transcript-client &:focus-visible': { backgroundColor: 'transparent !important', outline: 'none !important' },
              '#monkee-file-transcript-client &:active': { backgroundColor: `${alpha('#1976d2', 0.08)} !important` },
              '#monkee-file-transcript-client &:hover': { backgroundColor: `${alpha('#1976d2', 0.04)} !important` },
            }}
          >
            Criar pasta
          </Button>
        </Box>
      )}

      {/* Header da lista - responsivo */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fafafa'
        }}
      >
        {selectionMode && <Box sx={{ width: 42 }} />}
        <Box sx={{ width: 40 }} />
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, color: '#666' }}>
          Nome
        </Typography>
        <Typography variant="caption" sx={{ width: 140, fontWeight: 600, color: '#666', display: { xs: 'none', md: 'block' } }}>
          Data / Itens
        </Typography>
        <Typography variant="caption" sx={{ width: 80, fontWeight: 600, color: '#666', display: { xs: 'none', md: 'block' } }}>
          Tamanho
        </Typography>
        <Box sx={{ width: 100 }} />
      </Box>

      {/* Pastas */}
      {hasFolders && folders.map((folder, index) => (
        <FolderCard
          key={folder.id}
          folder={folder}
          view="list"
          onClick={onFolderClick || (() => {})}
          onRename={onFolderRename}
          onMove={onFolderMove}
          onDelete={onFolderDelete}
          onChangeColor={onFolderChangeColor}
          onDropItems={onDropItemsToFolder}
          animationDelay={index * 20}
        />
      ))}

      {/* Arquivos em lista */}
      {files.map(file => (
        <Box key={file.fileId} data-select-id={file.fileId}>
          <FileItemCard
            file={file}
            viewMode={viewMode}
            onOpen={onOpen}
            onDelete={onDelete}
            selectionMode={selectionMode}
            selected={selectedIds.has(file.fileId)}
            onSelect={onSelectFile}
            selectedFileIds={Array.from(selectedIds)}
            draggable
            onMoveToFolder={onMoveToFolder}
            onRename={onRename}
          />
        </Box>
      ))}
    </Box>
  )
}
